import React from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Stack, Typography, alpha, useTheme } from "@mui/material";

import { formatStepDuration, type TaskStep } from "./task-steps-helpers";
import { StepStatusIcon } from "./TaskStepIcon";

/**
 * Step header row with status icon, label, duration, and expand/collapse toggle.
 */
export function TaskStepHeader({
  step,
  isExpanded,
  onToggle,
}: {
  step: TaskStep;
  isExpanded: boolean;
  onToggle: (key: string) => void;
}): React.ReactElement {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      onClick={() => onToggle(step.key)}
      sx={{
        py: 1,
        px: 2,
        cursor: "pointer",
        "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) },
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.04)}`,
      }}
    >
      <StepStatusIcon status={step.status} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          noWrap
          sx={{
            color: step.status === "failed" ? "error.main" : "text.primary",
          }}
        >
          {step.label}
        </Typography>
      </Box>
      {formatStepDuration(step.startedAt, step.completedAt) && (
        <Typography variant="caption" color="text.secondary">
          {formatStepDuration(step.startedAt, step.completedAt)}
        </Typography>
      )}
      {step.htmlUrl && (
        <Typography
          variant="caption"
          component="a"
          href={step.htmlUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          sx={{ color: "primary.main", textDecoration: "underline" }}
        >
          View
        </Typography>
      )}
      <ExpandMoreIcon
        sx={{
          fontSize: 18,
          color: "text.disabled",
          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.2s",
        }}
      />
    </Stack>
  );
}
